import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
} from '@mui/joy';
import Button from '@mui/joy/Button';

import { ReactNode, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { useUpdateTagKey } from '@/hooks/useSwr.tsx';
import { ResourceTagKey } from '@/types/schema';

function ResourceTagKeyCrudForm(props: {
  defaultKey?: string;
  defaultDescription?: string;
  onSubmit: (data: { key: string; description?: string }) => void;
  onCancel: () => void;
}) {
  'use no memo';
  const form = useForm<{ key: string; description?: string }>({
    defaultValues: {
      key: props.defaultKey,
      description: props.defaultDescription,
    },
  });

  return (
    <form
      className="flex flex-col items-stretch gap-8"
      onSubmit={form.handleSubmit(props.onSubmit)}
    >
      <FormControl error={!!form.formState.errors.key?.message}>
        <FormLabel>Key</FormLabel>
        <Input placeholder="Key..." {...form.register('key')} />
        {form.formState.errors.key?.message && (
          <FormHelperText>{form.formState.errors.key?.message}</FormHelperText>
        )}
      </FormControl>
      <FormControl error={!!form.formState.errors.description?.message}>
        <FormLabel>Description</FormLabel>
        <Input placeholder="Description..." {...form.register('description')} />
        {form.formState.errors.description?.message && (
          <FormHelperText>
            {form.formState.errors.description?.message}
          </FormHelperText>
        )}
      </FormControl>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          onClick={props.onCancel}
          variant="outlined"
          color="neutral"
        >
          Cancel
        </Button>
        <Button type="submit" loading={form.formState.isSubmitting}>
          Update key
        </Button>
      </div>
    </form>
  );
}

export function ResourceTagKeyCrudModal(props: {
  className?: string;
  selectedKey?: ResourceTagKey;
  children: (props: {
    open: boolean;
    setOpen: (value: boolean) => void;
  }) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { trigger: updateResourceTagKey, error } = useUpdateTagKey();

  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog variant="outlined" sx={{ minWidth: 450 }}>
          <h2 className="mb-4 text-center font-medium">
            Edit "{props.selectedKey?.key}" resource tag key
          </h2>
          <Divider />

          <div className="mx-auto my-4 flex w-full max-w-[375px] flex-col gap-8">
            {error && <ExpandableErrorAlert error={error} />}

            <ResourceTagKeyCrudForm
              defaultKey={props.selectedKey?.key}
              defaultDescription={props.selectedKey?.description}
              onSubmit={(data) => {
                if (!props.selectedKey) return;
                updateResourceTagKey({
                  json: {
                    key: data.key,
                    description: data.description,
                  },
                  templateUrlParams: {
                    tagKeyId: props.selectedKey.id,
                  },
                });
                setOpen(false);
              }}
              onCancel={() => setOpen(false)}
            />
          </div>
        </ModalDialog>
      </Modal>

      {props.children({ open, setOpen })}
    </>
  );
}
